import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, TrendingUp, Users, Target, Award, Building2, Zap } from 'lucide-react';
export function EmployersPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-509">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-510">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-511">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-512">
            <span className="w-8 h-px bg-teal" data-id="element-513"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-514">
              Applications
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-515"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-516">
            Employers and Workforce Readiness
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-517">
            SyNeural's platform architecture is designed to help employers build
            stronger teams, more capable professionals, and more reliable
            leadership pipelines.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-518">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-519">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-520">
            Through CJI and NCJI, the platform provides a structured framework
            for understanding judgment, identifying readiness gaps, and
            connecting those gaps to meaningful development pathways that
            support stronger performance over time.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-521">
            In workforce environments, success depends on more than technical
            skill alone. Organizations rely on people who can think clearly,
            communicate well, regulate themselves under pressure, follow through
            consistently, adapt to complexity, and grow into larger
            responsibilities. SyNeural helps make those capabilities more
            visible and more actionable, creating a stronger foundation for
            workforce development, advancement, and organizational resilience.
          </p>
        </motion.div>
      </section>

      {/* Application Areas */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto space-y-20" data-id="element-522">
        {/* Workforce Development */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-523">
          <div className="flex items-center gap-4 mb-6" data-id="element-524">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-525">
              <Briefcase size={28} data-id="element-526" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-527">
              Workforce Development and Role Readiness
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-528">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-529">
              SyNeural can help employers better understand the non-technical
              capabilities that shape role readiness and long-term performance.
              These include reasoning quality, self-direction, emotional
              regulation, communication, execution discipline, and consequential
              thinking. By identifying where those capabilities are strong or
              underdeveloped, the platform can help organizations support
              employees more effectively and prepare them for broader
              responsibilities.
            </p>
          </div>
        </motion.div>

        {/* Leadership Development */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-530">
          <div className="flex items-center gap-4 mb-6" data-id="element-531">
            <div className="w-14 h-14 rounded-xl bg-navy-light flex items-center justify-center text-white shadow-lg" data-id="element-532">
              <Award size={28} data-id="element-533" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-534">
              Leadership Development
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-535">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-536">
              A major use of the platform in employer settings is leadership
              development. As people move into roles with more responsibility,
              judgment matters more. SyNeural can support the development of
              stronger managers, team leads, and executives by focusing on
              capabilities such as decision quality, systems thinking,
              communication under pressure, foresight, responsibility, and the
              ability to handle complexity without destabilizing teams or
              institutions.
            </p>
          </div>
        </motion.div>

        {/* Advancement and Internal Mobility */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-537">
          <div className="flex items-center gap-4 mb-6" data-id="element-538">
            <div className="w-14 h-14 rounded-xl bg-green-soft flex items-center justify-center text-white shadow-lg" data-id="element-539">
              <TrendingUp size={28} data-id="element-540" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-541">
              Advancement and Internal Mobility
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-542">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-543">
              Organizations often struggle to identify who is ready for growth
              and what specific areas need strengthening before advancement.
              SyNeural helps make this process more structured by showing where
              employees have strong potential, where capability bottlenecks
              exist, and what development pathways could support greater
              readiness. This makes the platform useful not only for assessment,
              but for internal mobility, progression planning, and long-term
              talent cultivation.
            </p>
          </div>
        </motion.div>

        {/* Execution and Team Performance */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-544">
          <div className="flex items-center gap-4 mb-6" data-id="element-545">
            <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center text-white shadow-lg" data-id="element-546">
              <Zap size={28} data-id="element-547" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-548">
              Execution, Communication, and Team Performance
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-549">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-550">
              Many organizational bottlenecks are driven not by lack of
              intelligence, but by weak execution, poor planning, communication
              breakdowns, reactivity, or limited judgment under pressure.
              SyNeural can help employers better understand these issues and
              connect them to practical support pathways. This can improve team
              dynamics, reliability, role fit, and broader organizational
              performance.
            </p>
          </div>
        </motion.div>

        {/* Beyond Traditional Evaluation */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-551">
          <div className="flex items-center gap-4 mb-6" data-id="element-552">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-553">
              <Target size={28} data-id="element-554" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-555">
              Employee Development Beyond Traditional Evaluation
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-556">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-557">
              Traditional performance reviews often capture outcomes without
              clearly explaining the underlying capabilities that produced them.
              SyNeural adds a deeper layer by helping organizations understand
              how employees think, how they respond to pressure, where they may
              be vulnerable to poor judgment, and where structured development
              could improve outcomes. This creates a more useful framework for
              coaching, support, and progression.
            </p>
          </div>
        </motion.div>

        {/* Organizational Resilience */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-558">
          <div className="flex items-center gap-4 mb-6" data-id="element-559">
            <div className="w-14 h-14 rounded-xl bg-navy-light flex items-center justify-center text-white shadow-lg" data-id="element-560">
              <Building2 size={28} data-id="element-561" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-562">
              Organizational Resilience and Institutional Strength
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-563">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-564">
              Organizations become stronger when they are staffed by people who
              can think clearly, adapt to complexity, and carry responsibility
              well. SyNeural supports this by helping employers strengthen the
              human layer beneath systems, processes, and leadership structures.
              That makes it relevant not only for individual development, but
              for organizational resilience, succession planning, and long-term
              institutional capability.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-565">
        <div className="max-w-4xl mx-auto" data-id="element-566">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-567">
            <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-568">
              Why It Matters for Employers
            </h2>
            <p className="text-xl leading-relaxed opacity-90" data-id="element-569">
              Employers need more than talent. They need people who can convert
              talent into judgment, judgment into execution, and execution into
              reliable contribution. SyNeural helps create a more structured way
              to understand and develop those capabilities. It supports stronger
              teams, more capable leadership, and a clearer path from potential
              to readiness across the workforce.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}